export const SRID_4326_DEGREES_PER_METER = 0.000009009 // WARNING: Based off of the equator, warps as you get closer to poles

export const KILOMETERS_TO_MILES_MULTIPLIER = 0.6213712;
export const METERS_PER_SECONDS_TO_MILES_PER_HOUR_MULTIPLIER = 2.236936;
export const METERS_TO_FEET_MULTIPLIER = 3.28084;
export const LITERS_TO_GALLONS_MULTIPLIER = 0.26417;
export const QUEUE_LENGTH_METERS_TO_NUM_VEHICLES_MULTIPLIER = 0.13123;

export const SMALL_INT_MAX = 32768;
export const SMALL_INT_MIN = -32768;
export const INT_MAX = 2147483647;
export const INT_MIN = -2147483648;

export const PRIORITY_ENUM = Object.freeze({
    protected: 'protected',
    permissive: 'permissive',
    free: 'free',
    stop_sign: 'stop_sign',
    right_on_red: 'right_on_red',
});

export const TURN_ENUM = Object.freeze({
    left: 'left',
    right: 'right',
    through: 'through',
    u: 'u',
});

// shared across detector subtypes
export const DETECTS_ENTITY_TYPE_ENUM = Object.freeze({
    vehicle: 'vehicle',
    bike: 'bike',
});
export const DETECTOR_VENDOR_TYPE = Object.freeze({
    ATSPM: 'ATSPM',
    'FHWA-CCS': 'FHWA-CCS',
});
export const DETECTOR_DATA_SOURCE_TYPE = Object.freeze({
    'UTAH-ATSPM-API': 'UTAH-ATSPM-API',
    'FHWA-CCS-volume-tabulation': 'FHWA-CCS-volume-tabulation',
});

// ATSPM detector
export const DETECTION_TYPE_ENUM = Object.freeze({
    stop_bar_presence: 'stop_bar_presence',
    lane_by_lane_with_speed_restriction: 'lane_by_lane_with_speed_restriction',
    lane_by_lane_count: 'lane_by_lane_count',
    advanced_count: 'advanced_count',
});

export const DETECTION_TYPE_LABELS = Object.freeze({
    stop_bar_presence: 'Stop Bar Presence',
    lane_by_lane_with_speed_restriction: 'Lane-by-lane with Speed Restriction',
    lane_by_lane_count: 'Lane-by-lane Count',
    advanced_count: 'Advanced Count',
});

export const DETECTOR_COMPASS_DIRECTION_TYPE = Object.freeze({
    E: 'E',
    W: 'W',
    N: 'N',
    S: 'S',
    NE: 'NE',
    NW: 'NW',
    SE: 'SE',
    SW: 'SW',
    NSC: 'NSC',
    EWC: 'EWC',
});
export const DETECTOR_MOVEMENT_TYPE_TYPE = Object.freeze({
    Left: 'Left',
    'Thru-Left': 'Thru-Left',
    Thru: 'Thru',
    'Thru-Right': 'Thru-Right',
    Right: 'Right',
    'Thru-Left-Right':'Thru-Left-Right',
    'Left-Right': 'Left-Right',
})

// CCS detector
export const DETECTOR_SENSOR_TYPE = Object.freeze({
    'Automatic vehicle identification (AVI)': 'Automatic vehicle identification (AVI)',
    'Bending plate': 'Bending plate',
    'Capacitance strip': 'Capacitance strip',
    'Capacitance mat/pad': 'Capacitance mat/pad',
    'Load cells (hydraulic or mechanical)': 'Load cells (hydraulic or mechanical)',
    'Fiber optic': 'Fiber optic',
    'Strain gauge or other sensors mounted on bridge components': 'Strain gauge or other sensors mounted on bridge components',
    'Human observation (manual)': 'Human observation (manual)',
    Infrared: 'Infrared',
    'In-line strain gauge load cell': 'In-line strain gauge load cell',
    'Laser/Lidar': 'Laser/Lidar',
    'Inductive loop': 'Inductive loop',
    Magnetometer: 'Magnetometer',
    Piezoelectric: 'Piezoelectric',
    'Quartz piezoelectric': 'Quartz piezoelectric',
    'Road tube': 'Road tube',
    'Sonic/acoustic': 'Sonic/acoustic',
    'Tape switch': 'Tape switch',
    Ultrasonic: 'Ultrasonic',
    'Video image': 'Video image',
    'Microwave (radar)': 'Microwave (radar)',
    'Radio wave (radar)': 'Radio wave (radar)',
    'Segmented strain gauge': 'Segmented strain gauge',
    Other: 'Other',
});

/*
can't create detector_calibration_of_weighting_system_type because it contains
a value longer than 63 characters, which is the limit for a type value -> changed column to TEXT
and we can enforce ENUM in API and ingest

ERROR:  invalid enum label "Combination of test trucks and trucks from the traffic stream (but not ASTM E1318) (yearly)"
DETAIL:  Labels must be 63 characters or less.
*/

export const DETECTOR_CALIBRATION_OF_WEIGHTING_SYSTEM_TYPE = Object.freeze({
    'ASTM Standard E1318 (yearly)': 'ASTM Standard E1318 (yearly)',
    'Subset of ASTM Standard E1318 (yearly)': 'Subset of ASTM Standard E1318 (yearly)',
    'Combination of test trucks and trucks from the traffic stream (but not ASTM E1318) (yearly)':
        'Combination of test trucks and trucks from the traffic stream (but not ASTM E1318) (yearly)',
    'Other sample of trucks from the traffic stream (yearly)': 'Other sample of trucks from the traffic stream (yearly)',
    'Statistical average of the steering axle of class nines (yearly)': 'Statistical average of the steering axle of class nines (yearly)',
    'LTPP Calibration Method (yearly)': 'LTPP Calibration Method (yearly)',
    'Static calibration (yearly)': 'Static calibration (yearly)',
    'Test trucks only (yearly)': 'Test trucks only (yearly)',
    Uncalibrated: 'Uncalibrated',
    'Other method (yearly)': 'Other method (yearly)',
});

export const DETECTOR_FUNCTIONAL_CLASSIFICATION_TYPE = Object.freeze({
    'Interstate Rural': 'Interstate Rural',
    'Principal Arterial – Other Freeways and Expressways Rural': 'Principal Arterial – Other Freeways and Expressways Rural',
    'Principal Arterial – Other Rural': 'Principal Arterial – Other Rural',
    'Minor Arterial Rural': 'Minor Arterial Rural',
    'Major Collector Rural': 'Major Collector Rural',
    'Minor Collector Rural': 'Minor Collector Rural',
    'Local Rural': 'Local Rural',
    'Interstate Urban': 'Interstate Urban',
    'Principal Arterial – Other Freeways and Expressways Urban': 'Principal Arterial – Other Freeways and Expressways Urban',
    'Principal Arterial – Other Urban': 'Principal Arterial – Other Urban',
    'Minor Arterial Urban': 'Minor Arterial Urban',
    'Major Collector Urban': 'Major Collector Urban',
    'Minor Collector Urban': 'Minor Collector Urban',
    'Local Urban': 'Local Urban',
});

export const DETECTOR_POSTED_ROUTE_SIGNING_TYPE = Object.freeze({
    'Not signed': 'Not signed',
    Interstate: 'Interstate',
    'U.S.': 'U.S.',
    State: 'State',
    'Off-Interstate Business Marker': 'Off-Interstate Business Marker',
    County: 'County',
    Township: 'Township',
    Municipal: 'Municipal',
    'Parkway Marker or Forest Route Marker': 'Parkway Marker or Forest Route Marker',
    'None of the above': 'None of the above',
});

export const OPTIMIZATION_JOB_STATUS = Object.freeze({
	'queued': 'queued',
	'running': 'running',
	'failed': 'failed',
	'in_review': 'in_review',
	'approved': 'approved',
	'deployed': 'deployed',
	'rejected': 'rejected',
	'deleted': 'deleted',

	'completed': 'completed', // legacy
});

export const OPTIMIZATION_TYPE = Object.freeze({
    offset: 'offset',
    split: 'split',
    offset_split: 'offset_split',
});

export const REQUEST_PENDING = 'REQUEST_PENDING';
export const REQUEST_COMPLETE = 'REQUEST_COMPLETE';
export const REQUEST_ERROR = 'REQUEST_ERROR';

// For a given Source, Mapbox requires unique "filter branch labels" across all Layers.
// These constants are used to make the Layers' filters unique and easier to work with empty id arrays.
export const DUMMY_FILTERED = 'DUMMY_FILTERED';
export const DUMMY_NOT_INTERACTABLE_ON_MAP = 'DUMMY_NOT_INTERACTABLE_ON_MAP';
export const DUMMY_ACTIVE = 'DUMMY_ACTIVE';
export const DUMMY_HOVERED = 'DUMMY_HOVERED';
export const DUMMY_SELECTED = 'DUMMY_SELECTED';

export const LONG_TERM_METRIC_KEYS = ['crashes', 'severe_crashes', 'fatalities', 'number_of_crashes', 'number_of_severe_crashes', 'number_of_fatalities'];


export const CUSTOMER_MODE_DEFAULTS  = [
	{
		id: 'ecd7081b-c9f0-780f-8ca2-05925773d033',
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations']
	},
	{
		id: 'b02c1d31-04e2-c042-9d55-95e7b8562cb3',
		default_mode: 'Safety',
		enabled_modes: ['Safety']
	},
	{
		id: 'c9e00f16-7915-618e-a3a9-58dd1e53711f',
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations']
	},
	{
		id: '8288a032-51f4-e234-4dd4-420af60fea98',
		default_mode: 'Safety',
		enabled_modes: ['Signal Operations', 'Safety']
	},
	{
		id: '13bbe020-2403-4cfe-2b61-daa5060bc82e',
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
	{
		id: '24718c01-426d-adf4-046b-5fa5eb48e808',
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations']
	},
	{
		id: '114733e0-ada8-063a-c478-74b77a356b7b',
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
	{
		id: '830f4d50-e6fd-45f1-5e70-0f05a490e492',
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
	{
		id: '58f0d8db-9c77-c291-7424-2b190e72a282',
		default_mode: 'Mobility',
		enabled_modes: ['Mobility']
	},
	{
		id: 'e6005245-f881-a4d8-ce6a-27b1488bf6bd',
		default_mode: 'Safety',
		enabled_modes: [ 'Safety']
	},
	{
		id: '931817ce-e1ef-a21f-f6f3-aad8e819e710',
		default_mode: 'Safety',
		enabled_modes: [ 'Safety', 'Mobility']
	},
    {
		id: '53f21cca-e330-daa1-b100-710cce26cfbd', //vdot
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
		id: '355707f4-5ba5-9c61-777b-d93cce8f5248',  //ncdot
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
		id: '8da4c41a-b1ef-f144-8158-039694e01a4b',  //nmdot
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations']
	},
    {
        id: 'aae206b1-6cf2-cccb-95c5-7fef0b7ad0f6',  //mndot
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'fa423d30-6035-7a7d-ec32-cada6fec9ad4',  //cfxway
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '4c6f0df8-7698-3775-57c2-1350d54402cf',  //BERNINILLO
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '7985cb55-0573-4964-af91-3b7c523b3f5e',  //DDOT
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'f846c1e1-2521-d17f-330f-b8659e299c7a',  //Mead and hunt
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'da7c5c1d-8982-35a4-255c-60cb17828e9c',  //Sequim
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '4b4131ab-29c7-d04b-5261-7f840acebc7c',  //Columbus Ohio
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'fb803efd-ea73-ecb8-5d9e-e9224cfb946c',  //Hoboken
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '58f48df1-96ee-09ff-aafb-85cffc11d1a5',  //Brownsville
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '79c08ebe-2387-568c-b581-f1c0a1682e45',  //LAMETRO
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '6a83e9f0-05ef-4434-7716-cad9f5c2a936',  //Loveland
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '7ccf3f83-a362-bbc1-3851-6b5c51368ede',  //HILLSBOROUGH
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'd3a222a7-85d0-3749-bb14-205445189efc',  //Washington State
		default_mode: 'Mobility',
		enabled_modes: ['Mobility']
	},
    {
        id: '44c2b1ea-fad9-dd27-6f68-fe293d3e8009',  //UMATILLA
		default_mode: 'Mobility',
		enabled_modes: ['Mobility']
	},
    {
        id: '0842fae3-0867-f81e-690e-9775f46f5490',  //UVA
		default_mode: 'Mobility',
		enabled_modes: ['Mobility']
	},
    {
        id: 'd295a781-f165-8b74-d8a3-2e0a3c971aaa',  //Palm coast smart north florida
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'c0f4c18e-77b9-2270-6c48-c58031c03ede',  // ASP
		default_mode: 'Safety',
		enabled_modes: ['Safety', 'Mobility']
	},
    {
        id: '71655eef-3d21-2073-5691-fa4190a77c7c',  // Ramudden
		default_mode: 'Safety',
		enabled_modes: ['Safety', 'Mobility']
	},
    {
        id: 'ff91f990-1d5c-603a-9ece-0b52a8f8cc49',  // DuPont
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '291a1bc4-dcb9-b69f-0c34-61db5799913c',  // Montgomery
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'c0d38823-937e-4db6-bb2d-ca9733e7cea0',  // VDOT2
		default_mode: 'Signal Operations',
		enabled_modes: ['Safety', 'Mobility']
	},
   {
        id: '743e23be-d0e9-328e-ea27-15594aa13519',  // Winchester
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '86a838f4-466f-79f8-3041-7ca1b452eb92',  // M&H/MNDOT
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},

    {
        id: '743e23be-d0e9-328e-ea27-15594aa13519',  // Winchester
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '71ff4b54-9668-542d-a1db-8bc02dd29ffb',  // VDOT SALEM
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'd4f3f041-24a9-5604-0371-786d7a31292a',  // la port
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '04aa5421-7fe7-794d-79de-a4a951589223',  // MENTOR
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '0571cd01-e087-3c7f-9d53-3c6da8fb2ed7',  // Alliant
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '165067fc-5cad-bc06-1034-d8c7dd8495e1',  // Citizen VAST 
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'b135f0c9-fe8b-923a-8f02-a7ab98c96565',  // Transpo
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '3e33a1be-5cb0-2af9-cb9a-cfd8e5f96c46',  // Miami
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '2b36bc93-385e-eda0-a42e-c3c29d735131',  // City of Las Vegas SPM Pilot via Westwood Professional Services
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '66b78c8a-6122-38cb-48c7-c217bf3fad12',  // Aurora
		default_mode: 'Mobility',
		enabled_modes: ['Mobility']
	},
    {
        id: '068d640b-4ec3-b4cc-1d3e-79d9445540dc',  // Hill and Smith - DelDOT API
		default_mode: 'Mobility',
		enabled_modes: ['Mobility']
	},
    {
        id: 'bd03fed3-5431-7d5a-5a5c-fd6e91fa99a5',  // Orange County
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: '056fabe8-aea2-0b81-c058-291500fa1874',  // Idaho Transportation Department - Signal Management Pilot
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
	},
    {
        id: 'b993fe36-75d3-41b5-6d7f-7e593d418a7c', // Metric Engineering - Miami PBSPM Near Ramp Meters
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '13fc5d33-df9c-cabe-1bee-596f140b8df6',  // Durham NC signal timing
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: 'eb3ba930-866d-9a8a-00e0-2d7c05f153a0',  // Pinellas County Pilot Customer
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '5b668ce8-19ef-94d8-1b26-c5d8c1a26433',  // Anne Arundel County
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: 'f9f69454-c686-20e1-d05b-f595409aaa13',  // City of Reno - ISPMs
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: 'fa1edbdb-5497-c052-9dbd-687396bebed4',  // SRF MNDOT Signal Retiming Luminus
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: 'b25d9cc2-bb2e-f4ce-3eea-0a533056d1b2', // Mississippi
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '9e04781a-13f3-1963-4b82-02788d027555', // Choice Engineering FDOT D6 ISPM & Optimus
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '626f70cb-2789-d150-7436-76a98ae159fc', // City of Arvada Mobility
		default_mode: 'Mobility',
		enabled_modes: ['Mobility']
    },
    {
        id: 'ad9ae6e5-1350-7429-afd5-49027d1bd9a8', // Timnath PBSPM
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: 'ccabfc5e-2b00-499f-5a7c-d7486613bbfe', // Arcadis GDOT Signal
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '189bda88-4d2f-74d6-60c5-d4d5d0e8939c', // Thorton, CO
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '63cba0a9-6882-13a5-ded0-dc3c90a92d38', // Courtenay
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '4e82b85d-6602-7d03-a079-5c32ed5cea87', // San Ramon
		default_mode: 'Signal Operations',
		enabled_modes: ['Signal Operations','Safety', 'Mobility']
    },
    {
        id: '28d1dab4-37d7-13a6-3c82-c1594a4e22f8', // Alamogordo 
    	default_mode: 'Mobility',
    	enabled_modes: ['Mobility']
    },
    {
        id: '752cb641-c16d-643b-8ca2-b993dc67db8e', // MRCOG 
    	default_mode: 'Mobility',
    	enabled_modes: ['Mobility']
    }

    
]

export const STATUS_FILE = {
    UPLOADED: 'uploaded',
    VALIDATED: 'validated',
    FAILED: 'failed',
};

export const FILE_UPLOAD_ERROR_CODE = {
    DUPLICATE: 'ERR-F004',
};

export const FILE_UPLOAD_ERROR_MESSAGE = {
    EXIST: 'This file already exist',
    UPLOAD_FAIL: 'Uploading has failed',
};

export const FILE_UPLOAD_MAX_SIZE = Number.isNaN(Number(process.env.FILE_UPLOAD_MAX_SIZE)) ? 1024 * 1024 * 1024 : Number(process.env.FILE_UPLOAD_MAX_SIZE);

export const COMPRESS_TYPE = ['zip'];

export const ENTITY_TYPES = {
	REGION : 'region',
	NETWORK : 'network',
	ROUTE: 'route',
	SIGNAL: 'signal',
	PHASE : 'phase',
	EDGE_GROUP : 'edge_group',
	INTERSECTION : 'intersection',
	DIRECTION : 'cardinal_directions',
	MEAN_LOS : 'mean_los',
}

export const SOLUTION_TYPE = {
	SIGNAL: 'signal_operations',
    SAFETY: 'safety',
}

export const METRIC_UNITS = [
    {
        VALUE: 'Percentage',
        UNIT: '%',
    },
    {
        VALUE: 'Seconds',
        UNIT: 's',
    },
];

export const OWNER_TYPES = {
    GLOBAL: 'global',
    CUSTOMER: 'customer',
    USER: 'user',
};

export const THRESHOLD_LEVEL = {
    LEVEL_0: 0,
    LEVEL_1: 1,
    LEVEL_2: 2,
    LEVEL_3: 3,
};

export const NOTIFICATION_SETTING_TYPES = {
    PERFORMANCE: 'metrics',
    REGION: 'region',
    ENTITY: 'entity_types',
};

export const NOTIFICATION_SETTING_STATUS = {
    ACTIVE: 'active',
    DISABLE: 'disable',
    INACTIVE: 'inactive'
};

export const NOTIFICATION_STATUS = {
    SEEN: 'seen',
    PUBLISHED: 'published',
};

export const USER_MANAGEMENT_STATUS = {
    ACTIVE: 'has_access',
    DISABLE: 'no_access',
};

export const USER_MANAGEMENT_SOLUTION_TYPE = {
    SIGNAL: 'signal_operations',
    SAFETY: 'safety',
    MOBILITY: 'mobility',
    ENVIRONMENTAL: 'environmental',
    ORIGIN_DESTINATION: 'origin_destination',
	CUSTOM_ENTITIES: 'custom_entities',
};

export const USER_MANAGEMENT_MODULE_TYPE = {
    ABACUS: 'abacus',
    CERBERUS: 'cerberus',
    OPTIMUS: 'optimus',
    LITMUS: 'litmus',
    REAL_TIME: 'realtime',
    OD_FILES: 'od_files',
    OD_REPORTS: 'od_reports',
	CUSTOM_NETWORKS: 'custom_networks',
	CUSTOM_EDGE_GROUPS: 'custom_edge_groups',
};

export const USER_MANAGEMENT_FEATURE_TYPE = {
    EXPORT_DATA: 'export_data',
    CREATE_REPORT: 'create_report_mailing_lists',
    CREATE_HEALTH: 'create_health_reports',
    MODIFY_TIMING: 'modify_Timing_plan_settings',
    GENERATE_OPTIMIZED: 'generate_optimized_timing_plans',
    TIME_SPACE: 'time_space',
    OD_FILES_ADD: 'add_region_files',
    OD_FILES_DELETE: 'delete_region_files',
    OD_FILES_EDIT: 'edit_region_files',
    OD_REPORTS_DELETE: 'delete_reports',
    OD_REPORTS_GENERATE: 'generate_reports',
    LITMUS_REPORTS_DELETE: 'delete_reports',
    LITMUS_REPORTS_GENERATE: 'generate_reports',
    LITMUS_REPORTS_EDIT: 'edit_reports',
	CUSTOM_ENTITIES_SAVE: 'custom_entities_save',
	CUSTOM_ENTITIES_EDIT: 'custom_entities_edit',
	CUSTOM_ENTITIES_DELETE: 'custom_entities_delete',
};

export const USER_MANAGEMENT_ROLE_TYPE = {
    SUPER_ADMIN: 'super_admin',
    ADMIN: 'admin',
    USER: 'user',
};

export const USER_METRICS_AVAILABILITY = Object.freeze({
    DETECTORS: 'detectors'
});

export const HEALTH_REPORTS_SETTING_TYPES = {
    PERFORMANCE: 'metrics',
    REGION: 'region',
    ENTITY: 'entity_types',
    EMAIl_SETTINGS: 'email_settings',
};

export const HEALTH_REPORTS_SETTING_STATUS = {
    ACTIVE: 'active',
    DISABLE: 'disable',
};

export const REPEAT_STATUS = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
    ANNUALLY: 'annually',
};

export const REPEAT_OPTIONS = [
    { label: 'Daily', value: REPEAT_STATUS.DAILY },
    { label: 'Weekly', value: REPEAT_STATUS.WEEKLY },
    { label: 'Monthly', value: REPEAT_STATUS.MONTHLY },
    { label: 'Quarterly', value: REPEAT_STATUS.QUARTERLY },
    { label: 'Annually', value: REPEAT_STATUS.ANNUALLY },
];

export const MERIDIEM_OPTIONS = [
    { label: 'am', value: 'am' },
    { label: 'pm', value: 'pm' },
];

export const FILE_TYPE_OPTIONS = [
    { label: 'XLSX', value: 'xlsx' },
    { label: 'CSV', value: 'csv' },
    { label: 'PDF', value: 'pdf' },
];

export const WEEKDAY_OPTIONS = [
    { label: 'Mo', value: 1 },
    { label: 'Tu', value: 2 },
    { label: 'We', value: 3 },
    { label: 'Th', value: 4 },
    { label: 'Fr', value: 5 },
    { label: 'Sa', value: 6 },
    { label: 'Su', value: 0 },
];

export const MONTHLY_OPTIONS = [
    { label: 'First', value: 1 },
    { label: 'Second', value: 2 },
    { label: 'Third', value: 3 },
    { label: 'Fourth', value: 4 },
    { label: 'Last', value: -1 },
];

export const QUARTERLY_OPTIONS = [
    { label: 'First', value: 1 },
    { label: 'Second', value: 2 },
    { label: 'Third', value: 3 },
    { label: 'Fourth', value: 4 },
    { label: 'Fifth', value: 5 },
    { label: 'Sixth', value: 6 },
    { label: 'Seventh', value: 7 },
    { label: 'Eighth', value: 8 },
    { label: 'Ninth', value: 9 },
    { label: 'Tenth', value: 10 },
    { label: 'Eleventh', value: 11 },
    { label: 'Twelfth', value: 12 },
    { label: 'Last', value: -1 },
];

export const MONTH_OPTIONS = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
];

export const DAY_PER_MONTH = [
    31, // January
    28, // February
    31, // March
    30, // April
    31, // May
    30, // June
    31, // July
    31, // August
    30, // September
    31, // October
    30, // November
    31, // December
];

export const DAY_OF_WEEK_KEYS = { MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6, SUNDAY: 0 };


export const CONTACT_KEYS = {
    SUPPORT_TEAM: 'support_team',
    CUSTOMER_SUCCESS: 'customer_success',
    KEY_ACCOUNT_MANAGER: 'key_account_manager',
    PROJECT_MANAGER: 'project_manager',
};

export const CONTACT_KEY_LABELS = {
    [CONTACT_KEYS.SUPPORT_TEAM]: 'Support Team',
    [CONTACT_KEYS.CUSTOMER_SUCCESS]: 'Customer Success',
    [CONTACT_KEYS.KEY_ACCOUNT_MANAGER]: 'Key Account Manager',
    [CONTACT_KEYS.PROJECT_MANAGER]: 'Project Manager',
};

export const LIST_HEAT_METRICS = [
    'max_pedestrian_count',
    'michelin_speeding_rate_events',
    'hard_braking_rate_events',
    'aggressive_acceleration_rate_events',
    'distracted_driver_rate_events',
    'michelin_crash_rate_events',
    'number_of_crashes',
    'number_of_severe_crashes',
    'number_of_fatalities',
];

export const LIST_HEAT_METRICS_LABEL = {
    max_pedestrian_count: 'Pedestrian Activity',
    michelin_speeding_rate_events: 'High Speed Events',
    hard_braking_rate_events: 'Hard Braking',
    aggressive_acceleration_rate_events: 'Aggressive Acceleration',
    distracted_driver_rate_events: 'Distracted Driving',
    michelin_crash_rate_events: 'Suspected Collisions',
    number_of_crashes: 'Crashes',
    number_of_severe_crashes: 'Severe Crashes',
    number_of_fatalities: 'Fatal Crashes',
};


export const LIST_HEAT_METRICS_MAP_WITH_SIDE_BAR_KEY = {
    pedestrian: 'max_pedestrian_count',
    speeding: 'michelin_speeding_rate_events',
    hard_braking: 'hard_braking_rate_events',
    aggressive_acceleration: 'aggressive_acceleration_rate_events',
    distracted_driver: 'distracted_driver_rate_events',
    suspected_collisions: 'michelin_crash_rate_events',
    crashes: 'number_of_crashes',
    severe_crashes: 'number_of_severe_crashes',
    fatalities: 'number_of_fatalities',
    number_of_crashes: 'number_of_crashes',
    number_of_severe_crashes: 'number_of_severe_crashes',
    number_of_fatalities: 'number_of_fatalities',
};

export const NOTIFICATION_TYPE = {
    HEALTH_SCORE: 'health_score',
    BASE_LINE_ALERT: 'baseline_alert',
    THRESHOLD_ALERT: 'threshold_alert',
    HEALTH_ALERT: 'health_alert',
    CUSTOM_ALERT: 'custom_alert',
    TREND_ALERT: 'trend_alert',
}

export const NOTIFICATION_LEVEL = {
    LOW: 'LOW',
    MEDIUM: 'MED',
    HIGH: 'HIGH'
}

export const LEVEL_OPTIONS = [
    { label: 'LOW', value: 1 },
    { label: 'MEDIUM', value: 2 },
    { label: 'HIGH', value: 3 },
];

export const CERBERUS_ENTITY_TYPES = {
    NETWORKS : 'networks',
    SIGNALS: 'signals',
    PHASES: 'phases',
    SIGNAL_SAFETY: 'signal_safety',
    PHASE_SAFETY: 'phase_safety',
    INTERSECTIONS: 'intersections',
    EDGE_GROUP_SAFETY: 'edge_group_safety',
    ROUTE_MOBILITY: 'route_mobility',
    EDGE_GROUP_MOBILITY: 'edge_group_mobility',
    TODS: 'tods',
    EXTERNAL_SIGNAL: 'external_signal',
    DETECTOR_HEALTH: 'detector_health'
}

export const CERBERUS_ENTITY_TYPES_TO_FILTER = {
    networks: 'network',
    signals: 'signal',
    phases : 'phase',
    signal_safety: 'signal',
    phase_safety: 'phase',
    edge_group_safety : 'edge_group',
    intersection_safety : 'intersection',
    route_mobility: 'edge_group',
    edge_group_mobility: 'edge_group',
    detector_health: CERBERUS_ENTITY_TYPES.DETECTOR_HEALTH
}
export const CERBERUS_ENTITY_TYPES_TO_FILTER_V2 = {
    networks: 'network',
    signals: 'signal',
    phases : 'phase',
    signal_safety: 'signal',
    phase_safety: 'phase',
    edge_group_safety : 'edge_group',
    intersection_safety : 'intersection',
    route_mobility: 'routes',
    edge_group_mobility: 'edge_group',
    environmental_networks: 'network',
    environmental_signals: 'signal',
    environmental_phases : 'phase',
    detector_health: CERBERUS_ENTITY_TYPES.DETECTOR_HEALTH,
    improvability: 'improvability',
    improvability_signals: 'improvability_signal'
}

export const MAP_WITH_Q = {
    regions: 'region_ids',
    networks: 'network_ids',
    signals: 'signal_ids',
    phases: 'phase_ids',
    intersections: 'intersection_ids',
    edge_groups: 'edge_group_ids',
    tods: 'tod_ids',
    external_signal: 'external_signal_ids',
    detector_types: 'detector_types',
    detector_statuses: 'detector_statuses'
};


export const CERBERUS_ENTITY_TYPES_TO_SOURCE = {
    networks: 'networks',
    signals: 'signals',
    phases : 'phases',
    signal_safety: 'signals',
    phase_safety: 'phases',
    edge_group_safety : 'edge_groups',
    intersection_safety : 'intersections',
    route_mobility: 'edge_groups',
    edge_group_mobility: 'edge_groups',
}

export const LITMUS_REPORT_TYPE = {
    SIGNALS: 'signals',
    INCIDENT: 'incident',
}

export const PERIOD_DAY_OPTION = [
    {
        value: 1,
        label: '1 day',
    },
    {
        value: 7,
        label: '7 days',
    },
    {
        value: 28,
        label: '28 days',
    },
    {
        value: 90,
        label: '90 days',
    },
    {
        value: 0,
        label: 'Custom Period Day',
    },
];

export const MAX_CUSTOM_NETWORK_AREA_SQUARE_METERS = 150_000_000 // ~12km^2
export const MAX_CUSTOM_EDGE_GROUP_LENGTH_METERS = 100_000 // 100km
export const MAX_SEARCH_TOKEN_LENGTH = 50

export const SPECIFIC_DATE_RANGE_VALUE = {
    ABSOLUTE: 'absolute',
    RELATIVE: 'relative',
};

export const DATE_RANGE_OPTIONS = [
    {
        key: 28,
        title: 'Latest 28 Days',
        is_df:true
    },
    {
        key: 7,
        title: 'Latest 7 Days',
        is_df:true
    },
    {
        key: 0,
        title: 'Custom Date Range',
        is_df:true
    },
];

export const SIGOPS_MARK  = {
    HIGH: 15,
    NORMAL: 35,
    LOW: 50
}

export const SAFETY_MARK  = {
    HIGH: 15,
    NORMAL: 35,
    LOW: 50
}

export const MAX_HEALTH_SCORE_THRESHOLD = 100;

export const HEALTH_THRESHOLD_ERR_MESSAGE = 'The health score must be within the range of 0 to 100.';

export const TIME_PERIOD_OPTIONS = [
    { id: 1, key: 1, title: 'DoD' },
    { id: 2, key: 7, title: 'WoW' },
    { id: 3, key: 28, title: 'MoM' },
    { id: 4, key: 91, title: 'QoQ' },
];


export const GET_TEXT_WITH_TIME_PERIOD = {
    1: 'previous day',
    7: 'past week',
    28: 'past month',
    91: 'past quarter',
};

export const INCIDENT_TYPE_SETS = {
	CRASH: new Set([
		'crash',
		'accident'
	]),
	FATALITY: new Set(['fatality']),
	DETECTOR_FAILURE: new Set(['detector_failure']),
	GENERAL: new Set([
		'unknown',
		'dangerous_conditions',
		'jam',
		'road_works',
		'broken_down_vehicle',
		'generic',
		'wrong-way vehicle',
		'pedestrian in the roadway',
		'stopped vehicle',
		'congestion',
		'slowed traffic',
		'queueing',
		'fast traffic',
	]),
	ROAD_CLOSURE: new Set([
		'lane_closed',
		'road_closed'
	]),
	WEATHER: new Set([
		'fog',
		'rain',
		'ice',
		'wind',
		'flooding',
		'low visibility'
	])
}

export const FEATURE_FLAGS = {
    CERBERUS_DETECTOR_HEALTH: 'cerberus-detector-health',
    USE_MVT_EDGE_GROUPS: 'use-mvt-edge-groups',
    // Opt-in flag that SHOWS the in-app Atlas Assistant UI (chat sidebar + AI Agents
    // settings tab). Default (flag absent) HIDES it, so production stays hidden unless
    // an environment explicitly enables it. The MCP server is unaffected either way.
    SHOW_ASSISTANT_UI: 'show-assistant-ui'
}

export const MODELLING_DEFAULT_VALID_FROM = Date.parse('2017-01-01T00:00:00Z')
export const MODELLING_DEFAULT_VALID_TO = null

/**
 * Data Monitoring -- general
 */
export const DATA_MONITOR_INGEST_STATUS = {
    NOT_STARTED: 'not-started',
    PARTIAL: 'partial',
    COMPLETE: 'complete'
}

export const DATA_MONITOR_INGEST_STATUS_LABELS = {
    NOT_STARTED: 'Ingest has not started',
    PARTIAL: 'Partial ingest period',
    COMPLETE: 'Ingest has completed'
}

/**
 * Data Monitoring -- validation rule configuration
 */
export const DATA_MONITOR_CONDITION_OPTIONS = [
    { title: 'Greater than', value: 'greater' },
    { title: 'Greater than or equal to', value: 'greater_or_equal' },
    { title: 'Less than', value: 'less' },
    { title: 'Less than or equal to', value: 'less_or_equal' },
    { title: 'Is equal to', value: 'equal' },
    { title: 'Is not equal to', value: 'not_equal' },
    { title: 'Is between', value: 'between' },
    { title: 'Is not between', value: 'not_between' },
];

export const DATA_MONITOR_LOGICAL_OPERATORS = [
    { title: 'AND', value: 'and' },
    { title: 'OR', value: 'or' },
];

export const DATA_MONITOR_SELECTION_RULE_OPTIONS = [
    { title: 'Validation Rule (Red Flag)', value: 'red_flag' },
    { title: 'Validation Rule (Orange Flag)', value: 'orange_flag' },
];

export const DATA_MONITOR_SELECTION_RULE_SETTING_OPTIONS = [
    { label: 'Validation Rule (Red Flag)', value: 'red_flag' },
    { label: 'Validation Rule (Orange Flag)', value: 'orange_flag' },
];

export const DATA_MONITOR_TIME_HORIZONS_OPTIONS = [
    { label: '1-day', value: 1 },
    { label: '7-day', value: 7 },
    { label: '28-day', value: 28 },
];

export const DATA_MONITOR_ENTITY_LEVEL_OPTIONS = [
    { value: 'network', label: 'Network' },
    { value: 'signal', label: 'Signal' },
    { value: 'phase', label: 'Phase' },
    { value: 'roadway', label: 'Roadways' },
];

/**
 * Insights
 */
export const INSIGHTS_FLOW_USER_ID_SHORT = '1111111111111111111111'
export const INSIGHTS_FLOW_CUSTOMER_ID_SHORT = '1111111111111111111111'


/**
 * Customer-specific configs
 */
export const ONE_YEAR_CRASH_DATE = ['pPYagA9jydDGJ4MJpw1MCA']
export const HAS_SEVERE_CRASHES = ['pPYagA9jydDGJ4MJpw1MCA']

/**
 * Crash years by customer for SigOps Safety crash metrics.
 * Keys are short (compressed) customer UUIDs. Values are arrays of calendar years (desc).
 * Used by both server (crash-metrics endpoints, cerberus joins) and frontend (cerberus columns, abacus).
 */
export const CRASH_YEARS_BY_CUSTOMER = {
	'38KmCMfyXxvcczo7iLBuM2': [ 2025, 2024], // FDOT
}

/**
 * Customers with a detector-health feed, gating the SigOps Abacus "Asset Health" section.
 * Keys are short (compressed) customer UUIDs, as in CRASH_YEARS_BY_CUSTOMER.
 * Seattle and MAG are included deliberately even though their feeds have stopped — the section
 * queries an exact measurement_date, so until their ingest resumes the table reports "No data".
 */
export const DETECTOR_HEALTH_CUSTOMERS = {
	'38KmCMfyXxvcczo7iLBuM2': true, // FDOT
	'o2thPxomwWDV5LkLKqpPE3': true, // Mississippi DOT
	'aGip3kVEW336mkdo8h8dwk': true, // City of San Ramon
	'43fwkpkMmGbEysPi6x2QAQ': true, // Thornton, CO
	'e9SyGMC5MppGDNyd1XZ7sA': true, // City of Loveland
	'kvJBWqAbhVSveQbqfVRECv': true, // FDOT D6/Choice Eng
	'vfgzhzYdKxQMh88TNZW9iZ': true, // City of Seattle - feed stopped 2026-06-29
	'3rktJXpgngYEXXnwswXxR1': true, // MAG - feed stopped 2024-12-08
}
